import { useState } from "react";
import { Share2, Copy, Check, MessageCircle, Gift } from "lucide-react";
import { toast } from "sonner";

const SITE_URL = "https://mdmsahayak.com";

// Psychologically-charged Odia referral message. Crafted to feel like a
// trusted teacher whispering to a colleague — not a marketing blast.
// Includes a future-discount tease for early supporters which compounds the
// "share now" motivation.
const SHARE_MESSAGE = `ନମସ୍କାର 🙏

MDM ଖାତା କାମ ପାଇଁ ଆପଣଙ୍କ ମୂଲ୍ୟବାନ ସମୟ ଆଉ ନଷ୍ଟ ହେବନି! ⏳

ଜଣେ ଶିକ୍ଷକଙ୍କ ଅସଲ କାମ ପିଲାଙ୍କୁ ପଢ଼ାଇବା ଓ ସେମାନଙ୍କ ଭବିଷ୍ୟତ ଗଢ଼ିବା. କିନ୍ତୁ ପ୍ରତିଦିନ MDM ର ଜଟିଳ ହିସାବ କିତାବ ଓ ଅଫିସ୍ ରିପୋର୍ଟ ଚିନ୍ତାରେ ଆମେ ମାନସିକ ଚାପରେ ରହୁଛେ. ଆଉ ଭୟ ନାହିଁ! ମୁଁ ନିଜେ MDM Sahayak ବ୍ୟବହାର କରି ମୋର ଘଣ୍ଟା ଘଣ୍ଟାର ସମୟ ବଞ୍ଚାଉଛି ଏବଂ ସମ୍ପୂର୍ଣ୍ଣ ଚିନ୍ତାମୁକ୍ତ ରହୁଛି।

ଏହା କୌଣସି ସରକାରୀ ଆପ୍ ନୁହେଁ, ବରଂ ଶିକ୍ଷକମାନଙ୍କ କାମକୁ ସହଜ କରିବା ପାଇଁ ଏକ ସମ୍ପୂର୍ଣ୍ଣ ସୁରକ୍ଷିତ ଓ ବ୍ୟକ୍ତିଗତ ଡିଜିଟାଲ୍ ସାଥୀ.

🔥 ଏହା କେମିତି ଆପଣଙ୍କ ସମୟ ଓ ସମ୍ମାନ ବଞ୍ଚାଇବ?
✅ ୧୦୦% ରିସ୍କ-ଫ୍ରି ଓ ସଠିକ୍ ହିସାବ — ଭୁଲ୍ ହେବାର ଭୟ ଆଉ ନାହିଁ.
✅ ମିନିଟ୍‌ରେ Quarterly UC ତିଆରି.
✅ ଦୈନିକ ଏଣ୍ଟ୍ରିରୁ ତୁରନ୍ତ ସୁନ୍ଦର ରିପୋର୍ଟ.
✅ ପାଚିକା ଓ ଭେଣ୍ଡର ହାତ ରସିଦ୍ ମିନିଟ୍‌ରେ.
✅ ଓଡ଼ିଆ ଭାଷାରେ ମୋବାଇଲ୍ ଓ କମ୍ପ୍ୟୁଟରରେ ସହଜ.

🎁 ବୋନସ୍ — ଯେଉଁ ଶିକ୍ଷକ ଆଜି join କରିବେ, ସେମାନଙ୍କ ପାଇଁ ଭବିଷ୍ୟତରେ ଆସୁଥିବା ସମସ୍ତ premium plans ଉପରେ ସ୍ୱତନ୍ତ୍ର ଡିସ୍କାଉଣ୍ଟ୍ ସୁରକ୍ଷିତ ରହିବ.

ଶିକ୍ଷକତାର ଗୌରବକୁ ଫେରାଇ ଆଣନ୍ତୁ, ଅଫିସ୍ କାମର ବୋଝକୁ କମାନ୍ତୁ!

👉 ଆଜି ହିଁ ଫ୍ରି-ରେ ଚେକ୍ କରନ୍ତୁ: ${SITE_URL}
📞 ସାହାଯ୍ୟ ପାଇଁ (WhatsApp): 8093414633`;

export default function ReferralShare({ user }) {
  const [copied, setCopied] = useState(false);

  // Personal touch — when a user is logged in, prepend a short hello so the
  // forward feels handwritten.
  const hello = user?.schoolName
    ? `ନମସ୍କାର — ମୁଁ ${user.schoolName} ରୁ। ଏହି ଡିଜିଟାଲ୍ ସାଥୀ ମୋର କାମ ସହଜ କରିଛି। ଆପଣ ମଧ୍ୟ ଚେକ୍ କରନ୍ତୁ 👇\n\n`
    : "";
  const fullMessage = hello + SHARE_MESSAGE;

  const waUrl = `https://wa.me/?text=${encodeURIComponent(fullMessage)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullMessage);
      setCopied(true);
      toast.success("Message copied — paste anywhere");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — long-press to copy");
    }
  };

  return (
    <section
      data-testid="referral-share-card"
      className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-clay/5 p-4 shadow-sm sm:p-5"
    >
      {/* Compact header — single row, no extra body copy. */}
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-600 text-white">
          <Share2 className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-emerald-700">Refer & Share · ସାଥୀ ସ୍କୁଲକୁ ଜଣାନ୍ତୁ</p>
          <h2 className="font-heading text-base font-bold text-ink-900 sm:text-lg">
            ଗୋଟିଏ tap ରେ ଶିକ୍ଷକ ବନ୍ଧୁଙ୍କ ସମୟ ବଞ୍ଚାନ୍ତୁ
          </h2>
        </div>
      </div>

      {/* Early-supporter discount hook — tiny pill that doesn't crowd the UI. */}
      <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-[0.7rem] font-semibold text-amber-900" data-testid="referral-discount-pill">
        <Gift className="h-3.5 w-3.5" />
        ଆଜି join କରୁଥିବା ଶିକ୍ଷକଙ୍କ ପାଇଁ ଭବିଷ୍ୟତ premium plans ଉପରେ special discount
      </div>

      {/* Big high-clickbait WhatsApp share button — full-width on mobile. */}
      <div className="mt-4 flex flex-wrap gap-2">
        <a
          href={waUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="referral-whatsapp-btn"
          className="group inline-flex flex-1 items-center justify-center gap-2.5 rounded-full bg-emerald-600 px-5 py-3 text-sm font-extrabold uppercase tracking-wide text-white shadow-md transition hover:bg-emerald-700 hover:shadow-lg active:scale-[0.99] sm:flex-none sm:text-base"
        >
          <MessageCircle className="h-5 w-5" />
          Share on WhatsApp · ଏବେ Share କରନ୍ତୁ
          <span className="ml-1 hidden rounded-full bg-white/20 px-2 py-0.5 text-[0.65rem] font-bold sm:inline">1 TAP</span>
        </a>
        <button
          type="button"
          onClick={copy}
          data-testid="referral-copy-btn"
          className="inline-flex items-center gap-2 rounded-full border-2 border-clay/30 bg-white px-4 py-2.5 text-xs font-bold text-clay shadow-sm transition hover:bg-clay/5"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy text"}
        </button>
      </div>

      {/* Collapsed preview — kept short so the card doesn't bloat. */}
      <details className="mt-3" data-testid="referral-preview-toggle">
        <summary className="cursor-pointer text-[0.7rem] font-semibold text-ink-700 hover:text-clay">
          ▸ ଯାହା ଶିକ୍ଷକ ବନ୍ଧୁଙ୍କୁ ପଠାଯିବ — preview କରନ୍ତୁ
        </summary>
        <pre
          className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-xl border border-border bg-sand-50 p-3 text-[0.72rem] leading-relaxed text-ink-900"
          data-testid="referral-share-preview"
        >{fullMessage}</pre>
      </details>
    </section>
  );
}
